'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageFrame } from '../ui'
import { ChevronDown, Loader2, RefreshCw, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import { ResizableTable, TableTd, TableCheckboxTd, TableCheckboxHeaderLabel, CHECKBOX_COL_WIDTH, CHECKBOX_COL_TH_CLASS, type ColumnDef } from '../ResizableTable'
import { useEmailTypes, useTenants } from './entriesApi'
import type { EmailLogItem, EmailTypeItem, TenantItem } from './entryTypes'

const PAGE_SIZE = 30

type Filters = { status: string; emailTypeIds: string[]; tenantId: string; campaign: string; since: string; until: string; q: string }
const EMPTY_FILTERS: Filters = { status: '', emailTypeIds: [], tenantId: '', campaign: '', since: '', until: '', q: '' }

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'sent' ? 'bg-success' : 'bg-destructive'
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white ${cls}`}>{status}</span>
}

function TenantTag({ name }: { name: string }) {
  return <span className="shrink-0 truncate rounded bg-signal-surface px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-signal">{name}</span>
}

// Native <select><option> can't render a styled tag next to the label, so this
// is a small custom dropdown just for Email Type pickers — every option shows
// which tenant it belongs to (if any) as a tag, so it's obvious at a glance
// when several tenants share similarly-named email types.
const UNASSIGNED_EMAIL_TYPE = 'none'

function EmailTypeSelect({
  theme,
  value,
  onChange,
  emailTypes,
  tenants,
  placeholder,
  noneLabel = '— None —',
  unassignedLabel,
}: {
  theme: ProcessedTheme
  value: string
  onChange: (value: string) => void
  emailTypes: EmailTypeItem[]
  tenants: TenantItem[]
  placeholder: string
  noneLabel?: string
  /** When set, adds a second, distinct option for "no email type assigned" (value becomes UNASSIGNED_EMAIL_TYPE) — separate from noneLabel, which clears the picker entirely. */
  unassignedLabel?: string
}) {
  const c = watchClasses(theme)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const tenantName = (t: EmailTypeItem) => (t.tenant_id ? tenants.find((x) => x.id === t.tenant_id)?.name : undefined)
  const isUnassigned = value === UNASSIGNED_EMAIL_TYPE
  const selected = emailTypes.find((t) => String(t.id) === value)

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-[30px] items-center gap-1.5 rounded border px-2 text-left text-[12px] ${c.input}`}
      >
        {selected ? (
          <span className="flex items-center gap-1.5">
            <span className="truncate">{selected.name}</span>
            {tenantName(selected) && <TenantTag name={tenantName(selected)!} />}
          </span>
        ) : isUnassigned ? (
          <span className="truncate">{unassignedLabel}</span>
        ) : (
          <span className={c.faint}>{placeholder}</span>
        )}
        <ChevronDown className={`ml-auto h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${c.faint}`} />
      </button>

      {open && (
        <div className={`absolute z-20 mt-1 max-h-64 min-w-[220px] overflow-y-auto rounded border shadow-notice ${c.panel} ${c.line}`}>
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false) }}
            className={`flex w-full items-center px-3 py-1.5 text-left text-[12px] hover:bg-secondary ${c.faint}`}
          >
            {noneLabel}
          </button>
          {unassignedLabel && (
            <button
              type="button"
              onClick={() => { onChange(UNASSIGNED_EMAIL_TYPE); setOpen(false) }}
              className={`flex w-full items-center px-3 py-1.5 text-left text-[12px] hover:bg-secondary ${c.text}`}
            >
              {unassignedLabel}
            </button>
          )}
          {emailTypes.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => { onChange(String(t.id)); setOpen(false) }}
              className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[12px] hover:bg-secondary ${c.text}`}
            >
              <span className="flex-1 truncate">{t.name}</span>
              {tenantName(t) && <TenantTag name={tenantName(t)!} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Filter version of the picker above — same tenant-tag treatment, but toggles
// values on/off without closing (checkboxes) and shows selections as removable
// chips in the trigger, since you often want "any of these types".
function EmailTypeMultiSelect({
  theme,
  values,
  onChange,
  emailTypes,
  tenants,
  placeholder,
  unassignedLabel,
}: {
  theme: ProcessedTheme
  values: string[]
  onChange: (values: string[]) => void
  emailTypes: EmailTypeItem[]
  tenants: TenantItem[]
  placeholder: string
  unassignedLabel?: string
}) {
  const c = watchClasses(theme)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const tenantName = (t: EmailTypeItem) => (t.tenant_id ? tenants.find((x) => x.id === t.tenant_id)?.name : undefined)
  const valueSet = new Set(values)
  const toggle = (v: string) => onChange(valueSet.has(v) ? values.filter((existing) => existing !== v) : [...values, v])
  const remove = (v: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(values.filter((existing) => existing !== v))
  }
  const labelFor = (v: string) => (v === UNASSIGNED_EMAIL_TYPE ? unassignedLabel : emailTypes.find((t) => String(t.id) === v)?.name) ?? v

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex min-h-[30px] w-full flex-wrap items-center gap-1 rounded border px-2 py-1 text-left text-[12px] ${c.input}`}
      >
        {values.length === 0 && <span className={c.faint}>{placeholder}</span>}
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded bg-signal-surface px-1.5 py-0.5 text-[11px] text-signal">
            {labelFor(v)}
            <X className="h-2.5 w-2.5 cursor-pointer opacity-70 hover:opacity-100" onClick={(e) => remove(v, e)} />
          </span>
        ))}
        <ChevronDown className={`ml-auto h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${c.faint}`} />
      </button>

      {open && (
        <div className={`absolute z-20 mt-1 max-h-64 min-w-[220px] overflow-y-auto rounded border shadow-notice ${c.panel} ${c.line}`}>
          {unassignedLabel && (
            <label className={`flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-secondary ${c.text}`}>
              <input type="checkbox" checked={valueSet.has(UNASSIGNED_EMAIL_TYPE)} onChange={() => toggle(UNASSIGNED_EMAIL_TYPE)} className="shrink-0" />
              {unassignedLabel}
            </label>
          )}
          {emailTypes.map((t) => (
            <label key={t.id} className={`flex w-full cursor-pointer items-center gap-1.5 px-3 py-1.5 text-left text-[12px] hover:bg-secondary ${c.text}`}>
              <input type="checkbox" checked={valueSet.has(String(t.id))} onChange={() => toggle(String(t.id))} className="shrink-0" />
              <span className="flex-1 truncate">{t.name}</span>
              {tenantName(t) && <TenantTag name={tenantName(t)!} />}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

async function fetchEmailLogs(offset: number, filters: Filters, limit: number = PAGE_SIZE) {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) })
  if (filters.status) params.set('status', filters.status)
  if (filters.emailTypeIds.length > 0) params.set('emailTypeId', filters.emailTypeIds.join(','))
  if (filters.tenantId) params.set('tenantId', filters.tenantId)
  if (filters.campaign) params.set('campaign', filters.campaign)
  if (filters.since) params.set('since', filters.since)
  if (filters.until) params.set('until', filters.until)
  if (filters.q) params.set('q', filters.q)

  try {
    const res = await fetch(`/api/campaigns/email-logs?${params.toString()}`, { credentials: 'include' })
    return await res.json()
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

async function applyBulkType(ids: string[], emailTypeId: number | null) {
  try {
    const res = await fetch('/api/campaigns/email-logs', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ids, emailTypeId }),
    })
    return await res.json()
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

export function EmailLogsView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const { emailTypes } = useEmailTypes()
  const { tenants } = useTenants()

  const [pendingFilters, setPendingFilters] = useState<Filters>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS)
  const [entries, setEntries] = useState<EmailLogItem[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkEmailTypeId, setBulkEmailTypeId] = useState('')
  const [bulkStatus, setBulkStatus] = useState<'idle' | 'saving' | 'error'>('idle')
  const [bulkError, setBulkError] = useState<string | undefined>()

  const load = useCallback(async (offset: number, filters: Filters, replace: boolean, limit: number = PAGE_SIZE) => {
    if (replace) setLoading(true)
    else setLoadingMore(true)
    setError(undefined)

    const result = await fetchEmailLogs(offset, filters, limit)
    if (result.success) {
      const page: EmailLogItem[] = result.data?.entries ?? []
      setEntries((prev) => (replace ? page : [...prev, ...page]))
      setTotal(result.data?.total ?? null)
      setHasMore(Boolean(result.data?.hasMore))
      if (replace) setSelectedIds(new Set())
    } else {
      setError(typeof result.error === 'string' ? result.error : 'Failed to load email logs')
    }
    setLoading(false)
    setLoadingMore(false)
  }, [])

  useEffect(() => {
    load(0, appliedFilters, true)
  }, [appliedFilters, load])

  const applyFilters = () => setAppliedFilters(pendingFilters)
  const clearFilters = () => {
    setPendingFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
  }

  const emailTypeName = (id?: number | null) => {
    if (!id) return '—'
    return emailTypes.find((t) => t.id === id)?.name ?? `#${id}`
  }

  const tenantNameForEmailType = (emailTypeId?: number | null) => {
    if (!emailTypeId) return undefined
    const tenantId = emailTypes.find((t) => t.id === emailTypeId)?.tenant_id
    if (!tenantId) return undefined
    return tenants.find((t) => t.id === tenantId)?.name
  }

  const allSelected = entries.length > 0 && selectedIds.size === entries.length
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(entries.map((e) => e.id)))
  }
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const columns: ColumnDef[] = useMemo(() => [
    {
      key: 'select',
      label: <TableCheckboxHeaderLabel checked={allSelected} onToggle={toggleSelectAll} />,
      initialWidth: CHECKBOX_COL_WIDTH,
      thClassName: CHECKBOX_COL_TH_CLASS,
    },
    { key: 'date', label: 'Date', initialWidth: 130 },
    { key: 'status', label: 'Status', initialWidth: 70 },
    { key: 'from', label: 'From', initialWidth: 180 },
    { key: 'to', label: 'To', initialWidth: 220 },
    { key: 'subject', label: 'Subject', initialWidth: 240 },
    { key: 'campaign', label: 'Campaign', initialWidth: 130 },
    { key: 'emailType', label: 'Email Type', initialWidth: 140 },
    { key: 'tenant', label: 'Tenant', initialWidth: 140 },
    { key: 'error', label: 'Error' },
  ], [allSelected, entries])

  const applyBulk = async () => {
    if (selectedIds.size === 0 || !bulkEmailTypeId) return
    setBulkStatus('saving')
    setBulkError(undefined)
    const emailTypeId = bulkEmailTypeId ? Number(bulkEmailTypeId) : null
    const result = await applyBulkType(Array.from(selectedIds), emailTypeId)
    if (!result.success) {
      setBulkStatus('error')
      setBulkError(typeof result.error === 'string' ? result.error : 'Failed to apply email type')
      return
    }
    setSelectedIds(new Set())
    setBulkEmailTypeId('')
    setBulkStatus('idle')
    // Patching email_type_id in place would leave rows visible that no longer
    // match the active filters (e.g. still showing under a "No Email Type"
    // filter after giving them a type) — reload from the server instead so the
    // list reflects what the current filters actually match now.
    load(0, appliedFilters, true)
  }

  return (
    <PageFrame padded={false} fill eyebrow="Administration · Logs" title="Email logs">
    <div className="flex h-full min-h-0 flex-col overflow-hidden p-4">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Status</span>
          <select
            className={`rounded border bg-transparent px-2 py-1.5 text-[12px] ${c.input}`}
            value={pendingFilters.status}
            onChange={(e) => setPendingFilters((p) => ({ ...p, status: e.target.value }))}
          >
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Email Type</span>
          <EmailTypeMultiSelect
            theme={theme}
            values={pendingFilters.emailTypeIds}
            onChange={(values) => setPendingFilters((p) => ({ ...p, emailTypeIds: values }))}
            emailTypes={emailTypes}
            tenants={tenants}
            placeholder="All"
            unassignedLabel="No Email Type"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Tenant</span>
          <select
            className={`rounded border bg-transparent px-2 py-1.5 text-[12px] ${c.input}`}
            value={pendingFilters.tenantId}
            onChange={(e) => setPendingFilters((p) => ({ ...p, tenantId: e.target.value }))}
          >
            <option value="">All</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Campaign</span>
          <Input
            className={`h-[30px] w-32 text-[12px] ${c.input}`}
            value={pendingFilters.campaign}
            onChange={(e) => setPendingFilters((p) => ({ ...p, campaign: e.target.value }))}
            placeholder="sibs_dispatch"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Since</span>
          <Input
            type="date"
            className={`h-[30px] text-[12px] ${c.input}`}
            value={pendingFilters.since}
            onChange={(e) => setPendingFilters((p) => ({ ...p, since: e.target.value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Until</span>
          <Input
            type="date"
            className={`h-[30px] text-[12px] ${c.input}`}
            value={pendingFilters.until}
            onChange={(e) => setPendingFilters((p) => ({ ...p, until: e.target.value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Search</span>
          <Input
            className={`h-[30px] w-56 text-[12px] ${c.input}`}
            value={pendingFilters.q}
            onChange={(e) => setPendingFilters((p) => ({ ...p, q: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder="Search any column…"
          />
        </div>

        <button
          onClick={applyFilters}
          className="h-[30px] rounded bg-primary text-primary-foreground px-3 text-[12px] font-semibold text-white hover:bg-signal"
        >
          Apply
        </button>
        <button
          onClick={clearFilters}
          className={`h-[30px] rounded border px-3 text-[12px] font-medium ${c.line} ${c.faint}`}
        >
          Clear
        </button>

        <span className={`ml-auto self-center text-[12px] ${c.faint}`}>
          {entries.length}{total !== null ? ` / ${total}` : ''} entries
        </span>
      </div>

      {selectedIds.size > 0 && (
        <div className={`mb-3 flex flex-wrap items-center gap-2 rounded border px-3 py-2 ${c.line} ${c.panel}`}>
          <span className={`text-[12px] font-semibold ${c.text}`}>{selectedIds.size} selected</span>
          <EmailTypeSelect
            theme={theme}
            value={bulkEmailTypeId}
            onChange={setBulkEmailTypeId}
            emailTypes={emailTypes}
            tenants={tenants}
            placeholder="Select email type…"
          />
          <button
            onClick={applyBulk}
            disabled={bulkStatus === 'saving' || !bulkEmailTypeId}
            className="flex items-center gap-1.5 rounded bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-signal disabled:opacity-50"
          >
            {bulkStatus === 'saving' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Apply Email Type
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className={`rounded border px-3 py-1.5 text-[12px] font-medium ${c.line} ${c.faint}`}
          >
            Clear selection
          </button>
          {bulkError && <span className="text-[12px] text-destructive">{bulkError}</span>}
        </div>
      )}

      {error && <div className="mb-2 text-[12px] text-destructive">{error}</div>}

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-signal" />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <ResizableTable columns={columns} stickyHeader theme={theme} isEmpty={entries.length === 0} emptyMessage="No email logs found.">
            {entries.map((entry) => (
              <tr key={entry.id} className={`border-b transition-colors hover:bg-secondary ${c.line}`}>
                <TableCheckboxTd checked={selectedIds.has(entry.id)} onToggle={() => toggleSelect(entry.id)} />
                <TableTd className={`text-[11px] ${c.faint}`}>{compactDate(entry.created_at)}</TableTd>
                <TableTd><StatusBadge status={entry.status} /></TableTd>
                <TableTd className="truncate">{entry.from}</TableTd>
                <TableTd className="truncate">{entry.to?.join(', ')}</TableTd>
                <TableTd className="truncate">{entry.subject}</TableTd>
                <TableTd className="truncate">{entry.campaign ?? '—'}</TableTd>
                <TableTd className="truncate">{emailTypeName(entry.email_type_id)}</TableTd>
                <TableTd className="truncate">
                  {tenantNameForEmailType(entry.email_type_id) ? <TenantTag name={tenantNameForEmailType(entry.email_type_id)!} /> : '—'}
                </TableTd>
                <TableTd className="truncate text-[11px] text-destructive">{entry.error ?? ''}</TableTd>
              </tr>
            ))}
          </ResizableTable>

          {hasMore && (
            <div className="mt-3 flex justify-center gap-2">
              <button
                onClick={() => load(entries.length, appliedFilters, false)}
                disabled={loadingMore}
                className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${c.line} ${c.faint}`}
              >
                {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Load more
              </button>
              <button
                onClick={() => load(entries.length, appliedFilters, false, 100)}
                disabled={loadingMore}
                className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${c.line} ${c.faint}`}
              >
                {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Load 100 more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </PageFrame>
  )
}
